import "./CardFace.css"
import { Minus, Plus } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useIdbState } from "../hooks/useIdbState"
import { getFactionName } from "../i18n/gameContent"
import { useI18n } from "../i18n/useI18n"
import { computeStatCost } from "../match/engine/costs"
import {
  ARTWORK_ASSET_STORAGE_KEY,
  ARTWORK_ASSET_STORAGE_VERSION,
  type ArtworkAssetStore,
  ArtworkAssetStoreSchema,
  EMPTY_ARTWORK_ASSET_STORE,
} from "../media/assets"
import { resolveArtworkUrl } from "../media/remote"
import type { CardArtwork, CardColor, SkillSelection } from "../schemas"
import { CostBadge } from "./CostBadge"
import { cardCssVars, FACTION_PALETTE } from "./cardTheme"
import { usePortalTarget } from "./PortalTarget"
import { AttackIcon, FACTION_ICON, HealthIcon } from "./SkillIcon"
import { SkillList } from "./SkillList"
import { SkillsEditor } from "./SkillsEditor"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CardSize = "sm" | "md" | "lg"

export type CardEditingCallbacks = {
  onNameChange: (name: string) => void
  onPrimaryColorChange: (color: CardColor) => void
  onSecondaryColorChange: (color: CardColor | undefined) => void
  onAttackChange: (attack: number) => void
  onHealthChange: (health: number) => void
  onSkillsChange: (skills: SkillSelection[]) => void
  forbiddenSkillIds?: ReadonlySet<string>
}

export type CardFaceProps = {
  name: string
  primaryColor: CardColor
  secondaryColor?: CardColor
  artwork?: CardArtwork
  artworkAssets?: ArtworkAssetStore
  stats: { attack: number; health: number }
  cost?: number
  costIsError?: boolean
  costBreakdown?: {
    stats: number
    skillDelta: number
    secondaryColorSurcharge?: number
  }
  skills?: SkillSelection[]

  // Hero display
  isHero?: boolean
  heroLevel?: 1 | 2 | 3

  // Play-mode HP (undefined = show base stats only)
  currentHp?: number

  // Play-mode state
  exhausted?: boolean
  showGuardBadge?: boolean

  // Layout
  size?: CardSize
  wide?: boolean
  hideSkills?: boolean

  // Interactivity
  onClick?: () => void
  selected?: boolean
  isTarget?: boolean
  dimmed?: boolean

  // Editing
  editing?: CardEditingCallbacks
  artworkEditorOverlay?: React.ReactNode

  className?: string
  style?: React.CSSProperties
}

const CARD_COLORS: CardColor[] = [
  "colorless",
  "red",
  "yellow",
  "green",
  "blue",
  "purple",
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function rectFromRef(ref: React.RefObject<HTMLElement | null>): DOMRect | null {
  return ref.current?.getBoundingClientRect() ?? null
}

// ---------------------------------------------------------------------------
// FactionPickerPopover
// ---------------------------------------------------------------------------

function FactionPickerPopover({
  current,
  exclude,
  allowColorless = true,
  onPick,
  onClear,
  onClose,
  anchorRect,
}: {
  current: CardColor
  exclude?: CardColor
  allowColorless?: boolean
  onPick: (color: CardColor) => void
  onClear?: () => void
  onClose: () => void
  anchorRect: DOMRect
}) {
  const { t } = useI18n()
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }
    const onOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    window.addEventListener("keydown", onKey, true)
    document.addEventListener("mousedown", onOutside, true)
    return () => {
      window.removeEventListener("keydown", onKey, true)
      document.removeEventListener("mousedown", onOutside, true)
    }
  }, [onClose])

  const portalTarget = usePortalTarget()
  return createPortal(
    <div
      className="cf-inline-popover cf-faction-picker"
      ref={popoverRef}
      style={{
        position: "fixed",
        top: anchorRect.bottom + 6,
        left: anchorRect.left,
        zIndex: 9999,
      }}
    >
      {CARD_COLORS.filter(
        (c) => c !== exclude && (allowColorless || c !== "colorless"),
      ).map((c) => {
        const Icon = FACTION_ICON[c]
        return (
          <button
            key={c}
            className={`cf-faction-pick-btn${c === current ? " cf-faction-pick-btn-active" : ""}`}
            onClick={() => {
              onPick(c)
              onClose()
            }}
            type="button"
            style={{ color: FACTION_PALETTE[c].muted }}
          >
            <Icon size="1em" strokeWidth={2} />
            {getFactionName(t, c)}
          </button>
        )
      })}
      {onClear && (
        <button
          className="cf-faction-pick-btn cf-faction-pick-btn-clear"
          onClick={() => {
            onClear()
            onClose()
          }}
          type="button"
        >
          ✕ {t("common.none")}
        </button>
      )}
    </div>,
    portalTarget,
  )
}

// ---------------------------------------------------------------------------
// StatEditPopover
// ---------------------------------------------------------------------------

function StatEditPopover({
  label,
  value,
  onChange,
  onClose,
  anchorRect,
  min,
  max,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  onClose: () => void
  anchorRect: DOMRect
  min: number
  max: number
}) {
  const { t } = useI18n()
  const popoverRef = useRef<HTMLDivElement>(null)
  const portalTarget = usePortalTarget()
  const options = Array.from({ length: max - min + 1 }, (_, idx) => min + idx)
  const decrementDisabled = value <= min
  const incrementDisabled = value >= max

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }
    const onOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    window.addEventListener("keydown", onKey, true)
    document.addEventListener("mousedown", onOutside, true)
    return () => {
      window.removeEventListener("keydown", onKey, true)
      document.removeEventListener("mousedown", onOutside, true)
    }
  }, [onClose])

  return createPortal(
    <div
      className="cf-inline-popover cf-stat-edit-popover"
      ref={popoverRef}
      style={{
        position: "fixed",
        top: anchorRect.bottom + 4,
        left: anchorRect.left,
        zIndex: 9999,
      }}
    >
      <span className="cf-stat-edit-label">{label}</span>
      <fieldset
        className="cf-stat-edit-stepper"
        aria-label={t("cardFace.stat.stepperAria", { label })}
      >
        <button
          className="cf-stat-edit-step"
          disabled={decrementDisabled}
          onClick={() => onChange(Math.max(min, value - 1))}
          type="button"
        >
          <Minus size={16} strokeWidth={2.25} />
        </button>
        <span className="cf-stat-edit-step-value">{value}</span>
        <button
          className="cf-stat-edit-step"
          disabled={incrementDisabled}
          onClick={() => onChange(Math.min(max, value + 1))}
          type="button"
        >
          <Plus size={16} strokeWidth={2.25} />
        </button>
      </fieldset>
      <div className="cf-stat-edit-options">
        {options.map((statValue) => (
          <button
            key={statValue}
            className={`cf-stat-edit-option${statValue === value ? " cf-stat-edit-option-active" : ""}`}
            onClick={() => {
              onChange(statValue)
              onClose()
            }}
            type="button"
          >
            {statValue}
          </button>
        ))}
      </div>
    </div>,
    portalTarget,
  )
}

// ---------------------------------------------------------------------------
// CostTooltip
// ---------------------------------------------------------------------------

function CostTooltip({
  breakdown,
  anchorRect,
}: {
  breakdown: {
    stats: number
    skillDelta: number
    secondaryColorSurcharge?: number
  }
  anchorRect: DOMRect
}) {
  const { t } = useI18n()
  const portalTarget = usePortalTarget()
  return createPortal(
    <div
      className="cf-cost-tooltip"
      style={{
        position: "fixed",
        top: anchorRect.bottom + 6,
        left: anchorRect.left,
        zIndex: 9999,
      }}
    >
      <div className="cf-cost-tooltip-row">
        <span>{t("cardFace.cost.stats")}</span>
        <CostBadge cost={breakdown.stats} size="m" variant="subtle" />
      </div>
      {breakdown.skillDelta > 0 && (
        <div className="cf-cost-tooltip-row">
          <span>{t("cardFace.cost.skills")}</span>
          <CostBadge
            cost={breakdown.skillDelta}
            isRelative
            size="m"
            variant="subtle"
          />
        </div>
      )}
      {(breakdown.secondaryColorSurcharge ?? 0) > 0 && (
        <div className="cf-cost-tooltip-row">
          <span>{t("cardFace.cost.dualFaction")}</span>
          <CostBadge
            cost={breakdown.secondaryColorSurcharge ?? 0}
            isRelative
            size="m"
            variant="subtle"
          />
        </div>
      )}
    </div>,
    portalTarget,
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LevelPips({ heroLevel }: { heroLevel?: 1 | 2 | 3 }) {
  const { t } = useI18n()
  if (!heroLevel) return null
  return (
    <span
      className="cf-level-pips"
      role="img"
      aria-label={t("cardFace.levelAria", { level: heroLevel })}
    >
      {([1, 2, 3] as const).map((n) => (
        <span
          key={n}
          className={`cf-level-pip${n <= heroLevel ? " cf-level-pip-on" : ""}`}
        />
      ))}
    </span>
  )
}

function CardFactionChips({
  editing,
  primaryColor,
  secondaryColor,
  onPrimaryPick,
  onSecondaryPick,
}: {
  editing?: CardEditingCallbacks
  primaryColor: CardColor
  secondaryColor?: CardColor
  onPrimaryPick: (rect: DOMRect) => void
  onSecondaryPick: (rect: DOMRect) => void
}) {
  const { t } = useI18n()
  const renderChipIcon = (color: CardColor) => {
    const Icon = FACTION_ICON[color]
    return <Icon size="0.9em" strokeWidth={2.2} />
  }
  const chipStyle = (color: CardColor) =>
    ({
      "--cf-chip-bg": `color-mix(in srgb, ${FACTION_PALETTE[color].base} 28%, transparent)`,
      "--cf-chip-color": FACTION_PALETTE[color].muted,
    }) as React.CSSProperties

  if (!editing) {
    return (
      <span className="cf-factions">
        <span
          className="cf-faction-chip"
          title={getFactionName(t, primaryColor)}
          style={chipStyle(primaryColor)}
        >
          {renderChipIcon(primaryColor)}
        </span>
        {secondaryColor && (
          <span
            className="cf-faction-chip"
            title={getFactionName(t, secondaryColor)}
            style={chipStyle(secondaryColor)}
          >
            {renderChipIcon(secondaryColor)}
          </span>
        )}
      </span>
    )
  }

  return (
    <span className="cf-factions">
      <button
        className="cf-faction-chip cf-faction-chip-editable"
        title={getFactionName(t, primaryColor)}
        style={chipStyle(primaryColor)}
        onClick={(e) => onPrimaryPick(e.currentTarget.getBoundingClientRect())}
        type="button"
      >
        {renderChipIcon(primaryColor)}
      </button>
      {secondaryColor ? (
        <button
          className="cf-faction-chip cf-faction-chip-editable"
          title={getFactionName(t, secondaryColor)}
          style={chipStyle(secondaryColor)}
          onClick={(e) =>
            onSecondaryPick(e.currentTarget.getBoundingClientRect())
          }
          type="button"
        >
          {renderChipIcon(secondaryColor)}
        </button>
      ) : (
        <button
          className="cf-faction-chip cf-faction-chip-add"
          title={t("cardFace.addSecondaryFaction")}
          onClick={(e) =>
            onSecondaryPick(e.currentTarget.getBoundingClientRect())
          }
          type="button"
        >
          +
        </button>
      )}
    </span>
  )
}

function CardHeader({
  editing,
  name,
  primaryColor,
  secondaryColor,
  isHero,
  heroLevel,
  cost,
  costIsError,
  costBreakdown,
  costRef,
  setCostTooltipRect,
  setPrimaryChipRect,
  setPrimaryPickerOpen,
  setSecondaryChipRect,
  setSecondaryPickerOpen,
}: {
  editing?: CardEditingCallbacks
  name: string
  primaryColor: CardColor
  secondaryColor?: CardColor
  isHero?: boolean
  heroLevel?: 1 | 2 | 3
  cost?: number
  costIsError?: boolean
  costBreakdown?: CardFaceProps["costBreakdown"]
  costRef: React.RefObject<HTMLSpanElement | null>
  setCostTooltipRect: (rect: DOMRect | null) => void
  setPrimaryChipRect: (rect: DOMRect | null) => void
  setPrimaryPickerOpen: (open: boolean) => void
  setSecondaryChipRect: (rect: DOMRect | null) => void
  setSecondaryPickerOpen: (open: boolean) => void
}) {
  const { t } = useI18n()

  return (
    <div className="cf-header">
      <CardFactionChips
        editing={editing}
        onPrimaryPick={(rect) => {
          setPrimaryChipRect(rect)
          setPrimaryPickerOpen(true)
        }}
        onSecondaryPick={(rect) => {
          setSecondaryChipRect(rect)
          setSecondaryPickerOpen(true)
        }}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
      {editing ? (
        <input
          className="cf-name-input"
          value={name}
          onChange={(e) => editing.onNameChange(e.target.value)}
          placeholder={t("common.unnamed")}
          type="text"
        />
      ) : (
        <span className="cf-name">{name || t("common.unnamed")}</span>
      )}
      {isHero && <LevelPips heroLevel={heroLevel} />}
      {cost !== undefined && (
        // biome-ignore lint/a11y/noStaticElementInteractions: mouse-only tooltip, no keyboard needed
        <span
          className="cf-cost"
          ref={costRef}
          onMouseEnter={
            costBreakdown
              ? () =>
                  setCostTooltipRect(
                    costRef.current?.getBoundingClientRect() ?? null,
                  )
              : undefined
          }
          onMouseLeave={
            costBreakdown ? () => setCostTooltipRect(null) : undefined
          }
          style={costBreakdown ? { cursor: "help" } : undefined}
        >
          <CostBadge
            cost={cost}
            isError={costIsError}
            size="l"
            variant="fill"
          />
        </span>
      )}
    </div>
  )
}

function CardStatsBadge({
  stats,
  currentHp,
  editing,
  showGuardBadge,
  onAttackClick,
  onHealthClick,
  atkRef,
  hpRef,
}: {
  stats: { attack: number; health: number }
  currentHp?: number
  editing?: CardEditingCallbacks
  showGuardBadge?: boolean
  onAttackClick: () => void
  onHealthClick: () => void
  atkRef: React.RefObject<HTMLSpanElement | null>
  hpRef: React.RefObject<HTMLSpanElement | null>
}) {
  const { t } = useI18n()
  const hp = currentHp ?? stats.health
  const hpPct = Math.max(0, Math.min(1, hp / stats.health))
  const showHpBar = currentHp !== undefined
  const hpBarLow = hpPct <= 0.35

  return (
    <>
      {showGuardBadge && (
        <span className="cf-guard-badge" title={t("common.guard")}>
          G
        </span>
      )}
      <div className="cf-stats-badge">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: role button set only when editing */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handled by StatEditPopover */}
        <span
          className={`cf-stat cf-stat-atk${editing ? " cf-stat-editable" : ""}`}
          role={editing ? "button" : undefined}
          onClick={editing ? onAttackClick : undefined}
          ref={atkRef}
        >
          <AttackIcon size="1em" strokeWidth={2.5} />
          {stats.attack}
        </span>
        <span className="cf-stat-divider" />
        {/* biome-ignore lint/a11y/noStaticElementInteractions: role button set only when editing */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handled by StatEditPopover */}
        <span
          className={`cf-stat cf-stat-hp${editing ? " cf-stat-editable" : ""}`}
          role={editing ? "button" : undefined}
          onClick={editing ? onHealthClick : undefined}
          ref={hpRef}
        >
          <HealthIcon size="1em" strokeWidth={2.5} />
          {showHpBar ? `${hp}/${stats.health}` : stats.health}
        </span>
      </div>
      <div className="cf-art-line" />
      {showHpBar && (
        <div className="cf-hp-bar-wrap">
          <div
            className={`cf-hp-bar${hpBarLow ? " cf-hp-bar-low" : ""}`}
            style={{ width: `${hpPct * 100}%` }}
          />
        </div>
      )}
    </>
  )
}

function CardArt({
  artworkUrl,
  artwork,
  artworkEditorOverlay,
  editing,
  showGuardBadge,
  stats,
  currentHp,
  onAttackClick,
  onHealthClick,
  atkRef,
  hpRef,
}: {
  artworkUrl?: string | null
  artwork?: CardArtwork
  artworkEditorOverlay?: React.ReactNode
  editing?: CardEditingCallbacks
  showGuardBadge?: boolean
  stats: { attack: number; health: number }
  currentHp?: number
  onAttackClick: () => void
  onHealthClick: () => void
  atkRef: React.RefObject<HTMLSpanElement | null>
  hpRef: React.RefObject<HTMLSpanElement | null>
}) {
  return (
    <div className="cf-art">
      {artworkUrl && (
        <img
          alt=""
          aria-hidden="true"
          className="cf-art-image"
          src={artworkUrl}
        />
      )}
      {editing && artworkEditorOverlay && (
        <div
          className={`cf-art-editor-overlay${artwork ? " cf-art-editor-overlay-filled" : " cf-art-editor-overlay-empty"}`}
        >
          {artworkEditorOverlay}
        </div>
      )}
      <CardStatsBadge
        atkRef={atkRef}
        currentHp={currentHp}
        editing={editing}
        hpRef={hpRef}
        onAttackClick={onAttackClick}
        onHealthClick={onHealthClick}
        showGuardBadge={showGuardBadge}
        stats={stats}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CardFace({
  name,
  primaryColor,
  secondaryColor,
  artwork,
  artworkAssets: providedArtworkAssets,
  stats,
  cost,
  costIsError = false,
  costBreakdown,
  skills = [],
  isHero,
  heroLevel,
  currentHp,
  exhausted,
  showGuardBadge,
  size = "md",
  wide = false,
  hideSkills = false,
  onClick,
  selected,
  isTarget,
  dimmed,
  editing,
  artworkEditorOverlay,
  className = "",
  style,
}: CardFaceProps) {
  const { t } = useI18n()
  const vars = cardCssVars(primaryColor, secondaryColor)
  const editorBaseCardCost =
    editing != null
      ? computeStatCost(stats.attack, stats.health) +
        (secondaryColor && primaryColor !== "colorless" ? 0.5 : 0)
      : undefined
  const [storedArtworkAssets] = useIdbState(
    ARTWORK_ASSET_STORAGE_KEY,
    ArtworkAssetStoreSchema,
    {
      defaultValue: EMPTY_ARTWORK_ASSET_STORE,
      version: ARTWORK_ASSET_STORAGE_VERSION,
    },
  )
  const artworkAssets = providedArtworkAssets ?? storedArtworkAssets
  const artworkUrl = useMemo(
    () =>
      resolveArtworkUrl(
        artwork,
        artworkAssets,
        size === "sm" ? "thumb" : "card_art",
      ),
    [artwork, artworkAssets, size],
  )

  // Popover state
  const [primaryPickerOpen, setPrimaryPickerOpen] = useState(false)
  const [secondaryPickerOpen, setSecondaryPickerOpen] = useState(false)
  const [statPopover, setStatPopover] = useState<{
    field: "attack" | "health"
    rect: DOMRect
  } | null>(null)
  const [costTooltipRect, setCostTooltipRect] = useState<DOMRect | null>(null)
  const [primaryChipRect, setPrimaryChipRect] = useState<DOMRect | null>(null)
  const [secondaryChipRect, setSecondaryChipRect] = useState<DOMRect | null>(
    null,
  )

  const atkRef = useRef<HTMLSpanElement>(null)
  const hpRef = useRef<HTMLSpanElement>(null)
  const costRef = useRef<HTMLSpanElement>(null)

  const openStatPopover = (
    field: "attack" | "health",
    getRect: () => DOMRect | null,
  ) => {
    const rect = getRect()
    if (rect) setStatPopover({ field, rect })
  }

  const wrapClasses = [
    "cf-wrap",
    `cf-size-${size}`,
    wide ? "cf-wrap-wide" : "",
    onClick ? "cf-clickable" : "",
    selected ? "cf-selected" : "",
    isTarget ? "cf-target" : "",
    dimmed ? "cf-dimmed" : "",
    exhausted ? "cf-exhausted" : "",
    hideSkills ? "cf-no-skills" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  const skillsContent = editing ? (
    <SkillsEditor
      skills={skills}
      baseCardCost={editorBaseCardCost}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      onChange={editing.onSkillsChange}
      forbiddenSkillIds={editing.forbiddenSkillIds}
    />
  ) : (
    <SkillList
      className="cf-skills"
      primaryColor={primaryColor}
      showDetailsOnHover={size === "lg"}
      skills={skills}
    />
  )

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: role is set conditionally when onClick is present
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-pressed only set when role="button" is also set
    <div
      className={wrapClasses}
      style={{ ...(vars as React.CSSProperties), ...style }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onClick()
            }
          : undefined
      }
      aria-pressed={onClick ? selected : undefined}
    >
      <div className="cf-inner">
        <CardHeader
          cost={cost}
          costBreakdown={costBreakdown}
          costIsError={costIsError}
          costRef={costRef}
          editing={editing}
          heroLevel={heroLevel}
          isHero={isHero}
          name={name}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          setCostTooltipRect={setCostTooltipRect}
          setPrimaryChipRect={setPrimaryChipRect}
          setPrimaryPickerOpen={setPrimaryPickerOpen}
          setSecondaryChipRect={setSecondaryChipRect}
          setSecondaryPickerOpen={setSecondaryPickerOpen}
        />

        {/* Wide: artwork left, skills right */}
        {wide ? (
          <>
            <div className="cf-art-col">
              <CardArt
                artwork={artwork}
                artworkEditorOverlay={artworkEditorOverlay}
                artworkUrl={artworkUrl}
                atkRef={atkRef}
                currentHp={currentHp}
                editing={editing}
                hpRef={hpRef}
                onAttackClick={() =>
                  openStatPopover("attack", () => rectFromRef(atkRef))
                }
                onHealthClick={() =>
                  openStatPopover("health", () => rectFromRef(hpRef))
                }
                showGuardBadge={showGuardBadge}
                stats={stats}
              />
            </div>
            {!hideSkills && (
              <SkillList
                className="cf-skills-col"
                primaryColor={primaryColor}
                showDetailsOnHover={size === "lg"}
                skills={skills}
              />
            )}
          </>
        ) : (
          <>
            <CardArt
              artwork={artwork}
              artworkEditorOverlay={artworkEditorOverlay}
              artworkUrl={artworkUrl}
              atkRef={atkRef}
              currentHp={currentHp}
              editing={editing}
              hpRef={hpRef}
              onAttackClick={() =>
                openStatPopover("attack", () => rectFromRef(atkRef))
              }
              onHealthClick={() =>
                openStatPopover("health", () => rectFromRef(hpRef))
              }
              showGuardBadge={showGuardBadge}
              stats={stats}
            />
            {!hideSkills && skillsContent}
          </>
        )}
      </div>

      {/* Popovers */}
      {editing && primaryPickerOpen && primaryChipRect && (
        <FactionPickerPopover
          current={primaryColor}
          exclude={secondaryColor}
          allowColorless
          onPick={(c) => editing.onPrimaryColorChange(c)}
          onClose={() => setPrimaryPickerOpen(false)}
          anchorRect={primaryChipRect}
        />
      )}
      {editing && secondaryPickerOpen && secondaryChipRect && (
        <FactionPickerPopover
          current={secondaryColor ?? primaryColor}
          exclude={primaryColor}
          allowColorless={false}
          onPick={(c) => editing.onSecondaryColorChange(c)}
          onClear={() => editing.onSecondaryColorChange(undefined)}
          onClose={() => setSecondaryPickerOpen(false)}
          anchorRect={secondaryChipRect}
        />
      )}
      {editing && statPopover && (
        <StatEditPopover
          label={
            statPopover.field === "attack"
              ? t("common.attack")
              : t("common.health")
          }
          value={statPopover.field === "attack" ? stats.attack : stats.health}
          max={7}
          min={statPopover.field === "attack" ? 0 : 1}
          onChange={(v) => {
            if (statPopover.field === "attack") editing.onAttackChange(v)
            else editing.onHealthChange(v)
          }}
          onClose={() => setStatPopover(null)}
          anchorRect={statPopover.rect}
        />
      )}
      {costBreakdown && costTooltipRect && (
        <CostTooltip breakdown={costBreakdown} anchorRect={costTooltipRect} />
      )}
    </div>
  )
}
